import { useTranslation } from "react-i18next";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckAlt, cilInfo, cilWarning } from "@coreui/icons";
import { PortfolioTrackerSplit, type TrackerStrategyDto } from "../../../api/hooks/usePortfolioTracker";
import { formatDay, formatMoney, formatPercent } from "../portfolio-tracker";
import {
  evenRows,
  formatAmountText,
  parseAmount,
  rowAmount,
  rowPercent,
  totalOf,
  withSplit,
  type Draft,
  type DraftCheck,
  type DraftRow,
  type Message,
} from "../tracker-setup";
import { TrackerStrategyIdent } from "./TrackerStrategyIdent";

interface MoneyAndDatesProps {
  strategies: Map<string, TrackerStrategyDto>;
  draft: Draft;
  onDraftChange: (draft: Draft) => void;
  check: DraftCheck;
  signalsFrom: string | null;
  asOf: string;
  saving: boolean;
  onBack: () => void;
  onSave: () => void;
}

const TOTAL_ICONS = { ok: cilCheckAlt, warn: cilInfo, err: cilWarning } as const;

/** Step 2: how much, split how, and since when. */
export function MoneyAndDates({
  strategies,
  draft,
  onDraftChange,
  check,
  signalsFrom,
  asOf,
  saving,
  onBack,
  onSave,
}: MoneyAndDatesProps) {
  const { t } = useTranslation("portfolioTracker");
  const say = (message: Message) => t(`setup.${message.key}`, message.values);
  const byAmount = draft.split === PortfolioTrackerSplit.Amount;
  const typedAmount = parseAmount(draft.amountText);
  const total = totalOf(draft);

  const changeRow = (index: number, change: Partial<DraftRow>) =>
    onDraftChange({
      ...draft,
      custom: draft.custom || "percentText" in change || "amountText" in change,
      rows: draft.rows.map((row, at) => (at === index ? { ...row, ...change } : row)),
    });

  // The second column says what the first comes to: dollars of the total by percent, a share of it by amount.
  const derived = (row: DraftRow) => {
    if (byAmount) {
      const amount = rowAmount(row);
      return total && total > 0 && amount > 0 ? formatPercent((amount / total) * 100) : "—";
    }
    const percent = rowPercent(row);
    return typedAmount ? formatMoney((typedAmount * (Number.isFinite(percent) ? percent : 0)) / 100) : "—";
  };

  const totalFigure = byAmount
    ? formatMoney(check.sum)
    : `${formatPercent(check.sum)}${typedAmount ? ` · ${formatMoney((typedAmount * Math.min(check.sum, 100)) / 100)}` : ""}`;

  return (
    <CCard className="tracker-step">
      <CCardHeader>
        <h2 className="h5 mb-0">{t("setup.title")}</h2>
        <p className="small text-body-secondary mb-0">{t("setup.description")}</p>
      </CCardHeader>
      <CCardBody className="tracker-setup-body">
        <div className="tracker-fields">
          {!byAmount && (
            <div>
              <CFormLabel htmlFor="tracker-amount" className="fw-semibold">
                {t("setup.amountLabel")}
              </CFormLabel>
              <div>
                <span className={`tracker-amount${check.amount ? " is-invalid" : ""}`}>
                  {t("setup.amountPrefix")}
                  <input
                    id="tracker-amount"
                    inputMode="decimal"
                    autoComplete="off"
                    aria-describedby="tracker-amount-hint"
                    aria-invalid={check.amount !== null}
                    value={draft.amountText}
                    onChange={(event) => onDraftChange({ ...draft, amountText: event.target.value })}
                    onBlur={() => {
                      if (typedAmount !== null) onDraftChange({ ...draft, amountText: formatAmountText(typedAmount) });
                    }}
                  />
                  {t("setup.amountSuffix")}
                </span>
              </div>
              <p id="tracker-amount-hint" className={`tracker-hint ${check.amount ? "text-danger" : ""}`}>
                {check.amount ? say(check.amount) : t("setup.amountHint")}
              </p>
            </div>
          )}
          <div>
            <CFormLabel htmlFor="tracker-start" className="fw-semibold">
              {t("setup.startLabel")}
            </CFormLabel>
            <CFormInput
              id="tracker-start"
              type="date"
              min={signalsFrom ?? undefined}
              max={asOf}
              disabled={draft.startsPerStrategy}
              invalid={check.start !== null}
              value={draft.start}
              onChange={(event) => onDraftChange({ ...draft, start: event.target.value })}
            />
            <p className={`tracker-hint ${check.start ? "text-danger" : ""}`}>
              {check.start
                ? say(check.start)
                : draft.startsPerStrategy
                  ? t("setup.startPerStrategyHint")
                  : signalsFrom
                    ? t("setup.startHint", { date: formatDay(signalsFrom) })
                    : ""}
            </p>
          </div>
        </div>

        <CFormCheck
          id="tracker-per-strategy"
          label={t("setup.perStrategy")}
          checked={draft.startsPerStrategy}
          onChange={(event) => {
            const startsPerStrategy = event.target.checked;
            onDraftChange({
              ...draft,
              startsPerStrategy,
              rows: startsPerStrategy ? draft.rows.map((row) => ({ ...row, start: draft.start })) : draft.rows,
            });
          }}
        />

        <div className="tracker-split-block">
          <div className="tracker-split-top">
            <h3 className="h6 mb-0">{t("setup.splitTitle")}</h3>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <CButtonGroup size="sm" className="btn-group-pill" role="group" aria-label={t("setup.splitBy")}>
                <CButton
                  color="primary"
                  variant={byAmount ? "outline" : undefined}
                  aria-pressed={!byAmount}
                  onClick={() => onDraftChange(withSplit(draft, PortfolioTrackerSplit.Percent))}
                >
                  {t("setup.byPercent")}
                </CButton>
                <CButton
                  color="primary"
                  variant={byAmount ? undefined : "outline"}
                  aria-pressed={byAmount}
                  onClick={() => onDraftChange(withSplit(draft, PortfolioTrackerSplit.Amount))}
                >
                  {t("setup.byAmount")}
                </CButton>
              </CButtonGroup>
              <CButton
                color="primary"
                variant="outline"
                size="sm"
                onClick={() =>
                  onDraftChange({
                    ...draft,
                    custom: false,
                    rows: evenRows(
                      draft.rows.map((row) => row.tradingStrategyId),
                      draft,
                    ),
                  })
                }
              >
                {t("setup.splitEvenly")}
              </CButton>
            </div>
          </div>
          <p className="tracker-hint mb-0">{byAmount ? t("setup.byAmountHint") : t("setup.byPercentHint")}</p>

          <div className={`tracker-split${draft.startsPerStrategy ? " has-dates" : ""}`}>
            <div className="tracker-split-row tracker-split-head">
              <span>{t("setup.columns.strategy")}</span>
              <span className="text-end">{byAmount ? t("setup.columns.amount") : t("setup.columns.allocation")}</span>
              <span className="text-end">{byAmount ? t("setup.columns.allocation") : t("setup.columns.firstBuy")}</span>
              {draft.startsPerStrategy && <span>{t("setup.columns.startedOn")}</span>}
            </div>
            {draft.rows.map((row, index) => {
              const strategy = strategies.get(row.tradingStrategyId);
              if (!strategy) return null;
              const error = check.rows[row.tradingStrategyId];
              const bad = byAmount ? !(rowAmount(row) > 0) : !(rowPercent(row) > 0);
              return (
                <div key={row.tradingStrategyId} className="tracker-split-row">
                  <TrackerStrategyIdent strategy={strategy} />
                  {byAmount ? (
                    <label className={`tracker-amount is-small tracker-split-input${bad ? " is-invalid" : ""}`}>
                      {t("setup.amountPrefix")}
                      <input
                        inputMode="decimal"
                        autoComplete="off"
                        aria-invalid={bad}
                        aria-label={t("setup.amountFor", { name: strategy.name })}
                        value={row.amountText}
                        onChange={(event) => changeRow(index, { amountText: event.target.value })}
                        onBlur={() => {
                          const amount = parseAmount(row.amountText);
                          if (amount !== null) changeRow(index, { amountText: formatAmountText(amount) });
                        }}
                      />
                      {t("setup.amountSuffix")}
                    </label>
                  ) : (
                    <CInputGroup size="sm" className="tracker-split-input">
                      <CFormInput
                        inputMode="decimal"
                        autoComplete="off"
                        className="text-end"
                        invalid={bad}
                        aria-label={t("setup.allocationFor", { name: strategy.name })}
                        value={row.percentText}
                        onChange={(event) => changeRow(index, { percentText: event.target.value })}
                      />
                      <CInputGroupText>%</CInputGroupText>
                    </CInputGroup>
                  )}
                  <span className="tracker-split-derived">{derived(row)}</span>
                  {draft.startsPerStrategy && (
                    <CFormInput
                      type="date"
                      size="sm"
                      className="tracker-split-date"
                      min={signalsFrom ?? undefined}
                      max={asOf}
                      aria-label={t("setup.startFor", { name: strategy.name })}
                      value={row.start}
                      onChange={(event) => changeRow(index, { start: event.target.value })}
                    />
                  )}
                  {error && <span className="tracker-split-error">{say(error)}</span>}
                </div>
              );
            })}
            <div className="tracker-split-row tracker-split-total">
              <span className={`tracker-total-message is-${check.total.tone}`}>
                <CIcon icon={TOTAL_ICONS[check.total.tone]} size="sm" />
                <span>{say(check.total.message)}</span>
              </span>
              <span className="tracker-total-figure">{totalFigure}</span>
            </div>
          </div>
        </div>
      </CCardBody>
      <CCardFooter className="tracker-step-foot">
        <CButton color="secondary" variant="outline" disabled={saving} onClick={onBack}>
          {t("setup.back")}
        </CButton>
        <CButton color="primary" disabled={!check.ok || saving} onClick={onSave}>
          {saving && <CSpinner size="sm" className="me-2" />}
          {t("setup.save")}
        </CButton>
      </CCardFooter>
    </CCard>
  );
}
